/**
 * BMRS (Balancing Mechanism Reporting Service) API Client
 * Documentation: https://bmrs.elexon.co.uk/api-documentation
 *
 * The BMRS API is free and public - no API key required
 */

const BMRS_BASE_URL = 'https://data.elexon.co.uk/bmrs/api/v1';

// Helper to format date for API (YYYY-MM-DD)
const formatDate = (date) => {
  if (!date) return undefined;
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

// Helper to get current settlement period (1-48, each 30 mins)
export const getCurrentSettlementPeriod = () => {
  const now = new Date();
  const minutesSinceMidnight = now.getHours() * 60 + now.getMinutes();
  return Math.floor(minutesSinceMidnight / 30) + 1;
};

// Helper to get settlement period time range
export const getSettlementPeriodTime = (period) => {
  const startMinutes = (period - 1) * 30;
  const endMinutes = period * 30;
  const formatTime = (mins) => {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };
  return {
    start: formatTime(startMinutes),
    end: formatTime(endMinutes),
  };
};

/**
 * Generic API fetch wrapper with error handling
 */
const fetchBMRS = async (endpoint, params = {}) => {
  const url = new URL(`${BMRS_BASE_URL}${endpoint}`);

  // Add query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, value);
    }
  });

  // Add format parameter for JSON response
  if (!url.searchParams.has('format')) {
    url.searchParams.append('format', 'json');
  }

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('BMRS API Error:', response.status, errorText.slice(0, 200));
      throw new Error(`BMRS API Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('BMRS API fetch error:', error);
    throw error;
  }
};

// ============================================
// GENERATION ENDPOINTS
// ============================================

/**
 * Get instantaneous generation by fuel type (FUELINST)
 * Returns current generation output broken down by fuel type
 */
export const getGenerationByFuelInstant = async () => {
  return fetchBMRS('/datasets/FUELINST');
};

/**
 * Get half-hourly generation by fuel type (FUELHH)
 * @param {string} settlementDate - Date in YYYY-MM-DD format
 * @param {number} settlementPeriod - Settlement period 1-48
 */
export const getGenerationByFuel = async (settlementDate, settlementPeriod) => {
  const params = {};
  if (settlementDate) params.settlementDate = formatDate(settlementDate);
  if (settlementPeriod) params.settlementPeriod = settlementPeriod;
  return fetchBMRS('/datasets/FUELHH', params);
};

/**
 * Get total generation outturn
 */
export const getGenerationOutturn = async (settlementDate, settlementPeriod) => {
  const params = {};
  if (settlementDate) params.settlementDate = formatDate(settlementDate);
  if (settlementPeriod) params.settlementPeriod = settlementPeriod;
  return fetchBMRS('/generation/outturn/summary', params);
};

/**
 * Get Physical Notifications for BMU generation levels
 * PN data shows what each BMU is generating/consuming
 */
export const getGenerationByBMU = async (settlementDate, settlementPeriod, bmUnit) => {
  const params = {};
  if (settlementDate) params.settlementDate = formatDate(settlementDate);
  if (settlementPeriod) params.settlementPeriod = settlementPeriod;
  if (bmUnit) params.bmUnit = bmUnit;
  return fetchBMRS('/datasets/PN', params);
};

/**
 * Get interconnector flows
 */
export const getInterconnectorFlows = async (settlementDate, settlementPeriod) => {
  const params = {};
  if (settlementDate) params.settlementDate = formatDate(settlementDate);
  if (settlementPeriod) params.settlementPeriod = settlementPeriod;
  return fetchBMRS('/generation/outturn/interconnectors', params);
};

// ============================================
// BALANCING / PHYSICAL DATA ENDPOINTS
// ============================================

/**
 * Get Physical Notifications (PN) dataset
 * @param {string} settlementDate - Date in YYYY-MM-DD format
 * @param {number} settlementPeriod - Settlement period 1-48 (optional)
 * @param {string} bmUnit - BMU ID (optional)
 */
export const getPhysicalNotifications = async (settlementDate, settlementPeriod, bmUnit) => {
  const params = {};
  if (settlementDate) params.settlementDate = formatDate(settlementDate);
  if (settlementPeriod) params.settlementPeriod = settlementPeriod;
  if (bmUnit) params.bmUnit = bmUnit;
  return fetchBMRS('/datasets/PN', params);
};

/**
 * Get Physical data per BMU (PN, QPN, MILS, MELS)
 */
export const getPhysicalDataByBMU = async (settlementDate, bmUnit) => {
  const dateStr = formatDate(settlementDate); // '2025-12-26'
  if (!dateStr || !bmUnit) {
    throw new Error('getPhysicalDataByBMU requires settlementDate and bmUnit');
  }

  // Full-day window for that settlement date
  const from = `${dateStr}T00:00:00Z`;
  const to = `${dateStr}T23:59:59Z`;

  const params = {
    bmUnit,
    from,
    to,
  };

  return fetchBMRS('/balancing/physical', params);
};

/**
 * Get all Physical data for date range
 */
export const getPhysicalDataAll = async (from, to, bmUnit) => {
  const params = {};
  if (from) params.from = formatDate(from);
  if (to) params.to = formatDate(to);
  if (bmUnit) params.bmUnit = bmUnit;
  return fetchBMRS('/balancing/physical/all', params);
};

// ============================================
// BID-OFFER DATA ENDPOINTS
// ============================================

/**
 * Get Bid-Offer Data (BOD) dataset
 * @param {string} settlementDate - Date in YYYY-MM-DD format
 * @param {number} settlementPeriod - Settlement period 1-48 (optional)
 * @param {string} bmUnit - BMU ID (optional)
 */
// Helper: build 30-minute window for a settlement period (API limit is 1 hour max)
const buildPeriodWindow = (settlementDate, settlementPeriod) => {
  const dateStr = formatDate(settlementDate); // YYYY-MM-DD
  if (!dateStr) {
    throw new Error('buildPeriodWindow requires settlementDate');
  }
  if (!settlementPeriod) {
    throw new Error('buildPeriodWindow requires settlementPeriod');
  }

  const base = new Date(`${dateStr}T00:00:00Z`);
  const start = new Date(base.getTime() + (settlementPeriod - 1) * 30 * 60 * 1000);
  const end = new Date(start.getTime() + 30 * 60 * 1000); // 30 minutes (1 settlement period)

  return {
    from: start.toISOString(),
    to: end.toISOString(),
  };
};

/**
 * Get Bid-Offer Data (BOD) dataset for one settlement period (optionally filtered by BMU)
 */
export const getBidOfferData = async (settlementDate, settlementPeriodFrom, settlementPeriodTo, bmUnit) => {
  if (!settlementPeriodFrom || !settlementPeriodTo || settlementPeriodFrom !== settlementPeriodTo) {
    throw new Error('getBidOfferData currently expects a single settlement period');
  }

  const { from, to } = buildPeriodWindow(settlementDate, settlementPeriodFrom);

  const params = {
    from,
    to,
    settlementPeriodFrom,
    settlementPeriodTo,
  };

  if (bmUnit) params.bmUnit = bmUnit;

  return fetchBMRS('/datasets/BOD', params);
};

/**
 * Get Bid-Offer data per BMU for one settlement period
 */
export const getBidOfferDataByBMU = async (settlementDate, bmUnit, settlementPeriod) => {
  if (!bmUnit) throw new Error('getBidOfferDataByBMU requires bmUnit');
  if (!settlementPeriod) throw new Error('getBidOfferDataByBMU requires settlementPeriod');
  return getBidOfferData(settlementDate, settlementPeriod, settlementPeriod, bmUnit);
};


/**
 * Get market-wide Bid-Offer data
 */
export const getBidOfferDataAll = async (settlementDate, settlementPeriod, bmUnit) => {
  const params = {};
  if (settlementDate) params.settlementDate = formatDate(settlementDate);
  if (settlementPeriod) params.settlementPeriod = settlementPeriod;
  if (bmUnit) params.bmUnit = bmUnit;
  return fetchBMRS('/balancing/bid-offer/all', params);
};

/**
 * Get Bid-Offer Acceptances (BOALF - Bid Offer Acceptance Level Flagged)
 * @param {string} settlementDate - Date in YYYY-MM-DD format
 * @param {number} settlementPeriod - Settlement period 1-48 (optional)
 * @param {string} bmUnit - BMU ID (optional)
 */
export const getBidOfferAcceptances = async (settlementDate, settlementPeriodFrom, settlementPeriodTo, bmUnit) => {
  const dateStr = formatDate(settlementDate);
  if (!dateStr) {
    throw new Error('getBidOfferAcceptances requires settlementDate');
  }

  const from = `${dateStr}T00:00:00Z`;
  const to = `${dateStr}T23:59:59Z`;

  const params = { from, to };

  if (settlementPeriodFrom) params.settlementPeriodFrom = settlementPeriodFrom;
  if (settlementPeriodTo) params.settlementPeriodTo = settlementPeriodTo;
  if (bmUnit) params.bmUnit = bmUnit;

  return fetchBMRS('/datasets/BOALF', params);
};

/**
 * Get all acceptances for a period
 */
export const getAcceptancesAll = async (settlementDate, settlementPeriod) => {
  const params = {};
  if (settlementDate) params.settlementDate = formatDate(settlementDate);
  if (settlementPeriod) params.settlementPeriod = settlementPeriod;
  return fetchBMRS('/balancing/acceptances/all', params);
};

// ============================================
// DEMAND ENDPOINTS
// ============================================

/**
 * Get Initial National Demand Outturn (INDO)
 */
export const getDemandOutturn = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return fetchBMRS('/datasets/INDO', params);
};

/**
 * Get Indicated Demand (INDOD)
 */
export const getIndicatedDemand = async (settlementDate, settlementPeriod) => {
  const params = {};
  if (settlementDate) params.settlementDate = formatDate(settlementDate);
  if (settlementPeriod) params.settlementPeriod = settlementPeriod;
  return fetchBMRS('/datasets/INDOD', params);
};

/**
 * Get Rolling System Demand
 */
export const getRollingSystemDemand = async () => {
  return fetchBMRS('/demand/rollingSystemDemand');
};

/**
 * Get demand forecast (National Day Ahead Demand)
 */
export const getDemandForecast = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return fetchBMRS('/datasets/NDFD', params);
};

// ============================================
// PRICING ENDPOINTS
// ============================================

/**
 * Get System Prices (Imbalance Prices)
 * Uses path parameters: /balancing/settlement/system-prices/{settlementDate}/{settlementPeriod}
 */
export const getSystemPrices = async (settlementDate, settlementPeriod) => {
  const date = formatDate(settlementDate) || formatDate(new Date());
  let endpoint = `/balancing/settlement/system-prices/${date}`;
  if (settlementPeriod) {
    endpoint += `/${settlementPeriod}`;
  }
  return fetchBMRS(endpoint, {});
};

/**
 * Get Market Index Prices
 */
export const getMarketIndexPrices = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return fetchBMRS('/datasets/MID', params);
};

/**
 * Get Day-Ahead Prices (Market Index Data)
 * Returns half-hourly day-ahead prices for GB market
 */
export const getDayAheadPrices = async (settlementDate) => {
  try {
    // Use Market Index Data for day-ahead prices
    const params = {
      settlementDate,
    };
    const response = await fetchBMRS('/datasets/MID', params);

    // Process the response to extract day-ahead prices by settlement period
    if (response && Array.isArray(response)) {
      return response.map(item => ({
        settlementPeriod: item.settlementPeriod,
        settlementDate: item.settlementDate,
        price: item.price || item.dataProviderPrice || item.imbalancePriceAmount || 0,
        dayAheadPrice: item.price || item.dataProviderPrice || 0,
        volume: item.volume || 0,
      }));
    }

    // If response is object with data property
    if (response && response.data && Array.isArray(response.data)) {
      return response.data.map(item => ({
        settlementPeriod: item.settlementPeriod,
        settlementDate: item.settlementDate,
        price: item.price || item.dataProviderPrice || item.imbalancePriceAmount || 0,
        dayAheadPrice: item.price || item.dataProviderPrice || 0,
        volume: item.volume || 0,
      }));
    }

    return [];
  } catch (error) {
    console.error('Failed to fetch day-ahead prices:', error);
    return [];
  }
};

// ============================================
// SYSTEM ENDPOINTS
// ============================================

/**
 * Get System Frequency
 */
export const getSystemFrequency = async () => {
  return fetchBMRS('/system/frequency');
};

/**
 * Get System Warnings
 */
export const getSystemWarnings = async () => {
  return fetchBMRS('/system/warnings');
};

/**
 * Get Indicated Margin
 */
export const getIndicatedMargin = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return fetchBMRS('/datasets/MELNGC', params);
};

// ============================================
// BMU REFERENCE DATA
// ============================================

/**
 * Get list of BMUs
 */
export const getBMUList = async () => {
  return fetchBMRS('/reference/bmunits/all');
};

/**
 * Get BMU details
 */
export const getBMUDetails = async (bmUnit) => {
  return fetchBMRS(`/reference/bmunits/${bmUnit}`);
};

/**
 * Get fuel types reference data
 */
export const getFuelTypes = async () => {
  return fetchBMRS('/reference/fueltypes');
};

// ============================================
// REMIT / OUTAGE DATA (includes capacity)
// ============================================

/**
 * Get REMIT outage data - includes installed capacity for BMUs
 * @param {string} bmUnit - Optional BMU ID to filter
 */
export const getREMITOutages = async (bmUnit) => {
  const params = {};
  if (bmUnit) {
    params.bmUnit = bmUnit;
  }
  return fetchBMRS('/remit/list/all', params);
};

/**
 * Get generation capacity data from REMIT
 * Returns aggregated capacity information for all BMUs
 */
export const getGenerationCapacity = async () => {
  // Use REMIT data to extract capacity
  const data = await fetchBMRS('/remit/list/all');

  // Transform to capacity lookup by BMU
  const capacityMap = {};
  if (Array.isArray(data)) {
    data.forEach((item) => {
      const bmUnit = item.bmUnit || item.affectedUnit;
      if (bmUnit && item.installedCapacity) {
        // Keep the highest capacity seen for each BMU
        if (!capacityMap[bmUnit] || item.installedCapacity > capacityMap[bmUnit]) {
          capacityMap[bmUnit] = item.installedCapacity;
        }
      }
    });
  }
  return capacityMap;
};

// ============================================
// ENHANCED DATA ENDPOINTS (for unicorn features)
// ============================================

/**
 * Get Actual Generation per BMU (B1610)
 * Shows actual output at unit level - critical for battery/storage visibility
 */
export const getActualGenerationByBMU = async (settlementDate, settlementPeriod) => {
  const params = {};
  if (settlementDate) params.settlementDate = formatDate(settlementDate);
  if (settlementPeriod) params.settlementPeriod = settlementPeriod;
  return fetchBMRS('/datasets/B1610', params);
};

/**
 * Get System Imbalance Volume (IMBALNGC)
 * Shows net imbalance volume - key for understanding system stress
 */
export const getSystemImbalance = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return fetchBMRS('/datasets/IMBALNGC', params);
};

/**
 * Get Wind Generation Forecast (WINDFOR)
 */
export const getWindForecast = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return fetchBMRS('/datasets/WINDFOR', params);
};

/**
 * Get Solar Generation Forecast
 */
export const getSolarForecast = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return fetchBMRS('/forecast/generation/wind-and-solar/day-ahead', params);
};

/**
 * Get Day-Ahead Generation Forecasts by fuel type
 */
export const getDayAheadGeneration = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return fetchBMRS('/datasets/DGWS', params);
};

/**
 * Get BSAD (Balancing Services Adjustment Data)
 * Shows system operator actions for balancing
 */
export const getBSADData = async (settlementDate, settlementPeriod) => {
  const params = {};
  if (settlementDate) params.settlementDate = formatDate(settlementDate);
  if (settlementPeriod) params.settlementPeriod = settlementPeriod;
  return fetchBMRS('/datasets/DISBSAD', params);
};

/**
 * Get Derived System-Wide Data (for margin calculations)
 */
export const getDerivedSystemData = async (settlementDate) => {
  const date = formatDate(settlementDate) || formatDate(new Date());
  return fetchBMRS(`/balancing/settlement/summary/${date}`);
};

/**
 * Get Loss of Load Probability (LoLP) - indicates system stress
 */
export const getLossOfLoadProbability = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return fetchBMRS('/datasets/LOLPDRM', params);
};

/**
 * Get De-rated Margin (system adequacy indicator)
 */
export const getDeratedMargin = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return fetchBMRS('/datasets/MELNGC', params);
};

/**
 * Helper: Get BMU metadata with fuel type classification
 * Identifies batteries, storage, and other asset types
 */
export const getBMUMetadata = async () => {
  const response = await fetchBMRS('/reference/bmunits/all');
  const bmuList = response?.data || response || [];

  // Build metadata lookup with fuel type classification
  const metadata = {};
  bmuList.forEach(bmu => {
    const id = bmu.bmUnit || bmu.nationalGridBmUnit;
    if (id) {
      metadata[id] = {
        bmUnit: id,
        name: bmu.name || bmu.registeredResourceName || id,
        fuelType: bmu.fuelType || 'UNKNOWN',
        leadPartyName: bmu.leadPartyName || '',
        // Identify storage/battery based on fuel type or naming patterns
        isStorage: (bmu.fuelType === 'PS' || bmu.fuelType === 'STORAGE' ||
                   /BESS|BATTERY|STORAGE|ESS/i.test(bmu.name || '')),
        isBattery: (/BESS|BATTERY|ESS/i.test(bmu.name || '') ||
                   bmu.fuelType === 'STORAGE'),
      };
    }
  });

  return metadata;
};

// Export all functions
const bmrsApi = {
  // Generation
  getGenerationByFuelInstant,
  getGenerationByFuel,
  getGenerationOutturn,
  getGenerationByBMU,
  getInterconnectorFlows,
  getActualGenerationByBMU,
  // Physical/Balancing
  getPhysicalNotifications,
  getPhysicalDataByBMU,
  getPhysicalDataAll,
  // Bid-Offer
  getBidOfferData,
  getBidOfferDataByBMU,
  getBidOfferDataAll,
  getBidOfferAcceptances,
  getAcceptancesAll,
  getBSADData,
  // Demand
  getDemandOutturn,
  getIndicatedDemand,
  getRollingSystemDemand,
  getDemandForecast,
  // Pricing
  getSystemPrices,
  getMarketIndexPrices,
  getDayAheadPrices,
  // System
  getSystemFrequency,
  getSystemWarnings,
  getIndicatedMargin,
  getSystemImbalance,
  getLossOfLoadProbability,
  getDeratedMargin,
  getDerivedSystemData,
  // Forecasts
  getWindForecast,
  getSolarForecast,
  getDayAheadGeneration,
  // Reference
  getBMUList,
  getBMUDetails,
  getFuelTypes,
  getBMUMetadata,
  // REMIT / Capacity
  getREMITOutages,
  getGenerationCapacity,
  // Helpers
  getCurrentSettlementPeriod,
  getSettlementPeriodTime,
};

export default bmrsApi;
